import { takeLatest, call, put, select } from "redux-saga/effects";
import { config } from "hub-redux-auth";

import API_ENDPOINTS from "@services/apiConfig";
import AuthManager from "@services/AuthManager";
import axios from "axios";
import { ALLOWED_QUERY_PARAMS, AUTH_QUERY_PARAMS } from "@constants/queryParams";
import { getQueryParams, isASCII } from "@constants/utils";

import { uiActions, hubActions } from ".";

//  Action types
export const actionTypes = {
  fetch: "merchantDetails/INITIATE",
  fetchFailed: "merchantDetails/FETCH_FAILED",
  fetchFulfilled: "merchantDetails/FETCH_FULFILLED",
  saveAllowedQueryParams: "SAVE_ALLOWED_QUERY_PARAMS",
  updateMerchantDetails: "merchantDetails/UPDATE",
  fetchAccountDetails: "accountDetails/INITATE",
  fetchKYCStatus: "kycStatus/INITATE",
};

const initialState = {
  fetching: false,
  fetched: false,
  response: null,
  errResponse: null,
};

//  Reducer
export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.fetch: {
      return {
        ...initialState,
        fetching: true,
      };
    }
    case actionTypes.fetchFulfilled: {
      return {
        ...initialState,
        fetched: true,
        response: action.payload,
      };
    }
    case actionTypes.fetchFailed: {
      return {
        ...initialState,
        fetched: true,
        errResponse: action.payload,
      };
    }
    case actionTypes.updateMerchantDetails: {
      return {
        ...state,
        response: { ...state.response, ...action.payload },
      };
    }
    default: {
      return state;
    }
  }
};

//  Selectors
export const selectors = {
  merchantDetailsFetched: (state) => state.onboardingApp.merchant.fetched,
  getMerchantDetails: (state) => state.onboardingApp.merchant.response,
  getHolderName: (state) =>
    state.onboardingApp.merchant.response &&
    state.onboardingApp.merchant.response.bank_detail &&
    state.onboardingApp.merchant.response.bank_detail.holder_name,
  getNameOnPanCard: (state) =>
    state.onboardingApp.merchant.response ? state.onboardingApp.merchant.response.pancard_name : null,
  getBankVerificationStatus: (state) => {
    const merchantDetails = state.onboardingApp.merchant.response || {
      bank_detail: {},
    };
    const {
      bank_detail: { bank_verification_status },
    } = merchantDetails;
    return bank_verification_status;
  },
  getPennyStatus: (state) => {
    const merchantDetails = state.onboardingApp.merchant.response || {
      bank_detail: {},
    };
    const {
      bank_detail: { penny_deposit_status },
    } = merchantDetails;
    return penny_deposit_status;
  },
  getBankDetails: (state) => {
    const merchantDetails = state.onboardingApp.merchant.response || {
      bank_detail: {},
    };
    const { bank_detail } = merchantDetails;
    return bank_detail;
  },
  getMerchantEmail: (state) => state.onboardingApp.merchant.response && state.onboardingApp.merchant.response.email,
  getMerchantMobile: (state) =>
    state.onboardingApp.merchant.response && state.onboardingApp.merchant.response.registered_mobile,
  getMerchantBusinessDetails: (state) => {
    const {
      business_category_id,
      business_sub_category_id,
      gst_number,
      monthly_expected_volume,
      business_entity_id,
      business_name,
      pancard_name,
      business_pan_name_match,
      shop_number,
      area_code,
    } = state.onboardingApp.merchant.response || {};
    return {
      business_entity_id,
      business_category_id,
      business_sub_category_id,
      gst_number,
      monthly_expected_volume,
      business_name,
      pancard_name,
      business_pan_name_match,
      shop_number,
      area_code,
    };
  },
  getWebsiteDetails: (state) => {
    const merchantDetails = (state.onboardingApp.merchant.response &&
      state.onboardingApp.merchant.response.website_detail) || {
      android_url: "",
      ios_url: "",
      website_url: "",
    };
    const websiteDetails = {
      android_url: merchantDetails.android_url == null ? "" : merchantDetails.android_url,
      ios_url: merchantDetails.ios_url == null ? "" : merchantDetails.ios_url,
      website_url: merchantDetails.website_url == null ? "" : merchantDetails.website_url,
      website_approval_status: merchantDetails?.website_approval_status,
    };
    return websiteDetails;
  },
  getMerchantIntegrationType: (state) => {
    return (state.onboardingApp.merchant.response && state.onboardingApp.merchant.response.integration_type) || null;
  },
  getDocumentStatus: (state) => state.onboardingApp.merchant.response && state.onboardingApp.merchant.response.document_status,
  getAccountUuid: (state) =>
    state.onboardingApp.merchant.response && state.onboardingApp.merchant.response.account_uuid,
  getSigningAuthorityDetails: (state) =>
    state.onboardingApp.merchant.response &&
    state.onboardingApp.merchant.response.account &&
    state.onboardingApp.merchant.response.account.signatory_contact_details,
  getRegistrationAddress: (state) => state.onboardingApp.merchant?.response?.registration_address,
  getOperatingAddress: (state) => state.onboardingApp.merchant?.response?.operating_address,
  getSavedKycAddress: (state) =>
    state.onboardingApp.merchant.response && state.onboardingApp.merchant.response.saved_kyc_address,
  getIsServiceAgreementEsigned: (state) =>
    state.onboardingApp.merchant.response && state.onboardingApp.merchant.response.is_service_agreement_esigned,
};

//  Actions
export const actions = {
  fetch: () => ({ type: actionTypes.fetch }),
  fetchFulfilled: (response) => ({
    type: actionTypes.fetchFulfilled,
    payload: response,
  }),
  fetchFailed: (errResponse) => ({
    type: actionTypes.fetchFailed,
    payload: errResponse,
  }),
  saveAllowedQueryParams: (location) => ({
    type: actionTypes.saveAllowedQueryParams,
    payload: location,
  }),
  updateMerchantDetails: (merchantDetails) => ({
    type: actionTypes.updateMerchantDetails,
    payload: merchantDetails,
  }),
  fetchAccountDetails: () => ({
    type: actionTypes.fetchAccountDetails,
  }),
  fetchKYCStatus: () => ({ type: actionTypes.fetchKYCStatus }),
};

//  Workers
function* fetchWorker() {
  try {
    const user = AuthManager.getUser() || { merchant_uuid: null };
    const {
      data: { merchants },
    } = yield call([axios, "get"], API_ENDPOINTS.AUTH.MERCHANTS(user.merchant_uuid)) || {
      data: { merchants: [] },
    };
    const [merchantDetails] = merchants;
    yield put(actions.fetchFulfilled(merchantDetails));
    yield put(hubActions.fetchHubToken());
    yield put(actions.fetchAccountDetails());
    AuthManager.persistUserToLocalStorage({
      merchant_uuid: merchantDetails ? merchantDetails.uuid : null,
      mid: merchantDetails ? merchantDetails.mid : null,
      name: merchantDetails ? merchantDetails.name : null,
      product: merchantDetails ? merchantDetails.product : null,
      dashboard_preference: merchantDetails ? merchantDetails.dashboard_preference : null,
      uuid: merchantDetails ? merchantDetails.uuid : null,
      email: merchantDetails ? merchantDetails.email : null,
      cs_plan: merchantDetails ? merchantDetails.cs_plan : null,
      is_cs_eligible: merchantDetails ? merchantDetails.is_cs_eligible : null,
    });
  } catch (err) {
    const errResponse = yield err.response;
    console.log(errResponse, err);
    yield put(actions.fetchFailed(errResponse));
  }
}

function* saveAllowedQueryParamsWorker(action) {
  const location = action.payload;
  let queryParams = {};
  ALLOWED_QUERY_PARAMS.forEach((key) => {
    const value = getQueryParams(key, location);
    if (value && isASCII(decodeURI(value))) {
      queryParams[key] = value;
    }
  });
  config.set('ALLOWED_QUERY_PARAMS', queryParams);
  const authToken = getQueryParams(AUTH_QUERY_PARAMS.authToken, location);
  const merchant_uuid = getQueryParams(AUTH_QUERY_PARAMS.merchantUuid, location);
  if (authToken && merchant_uuid) {
    const tokenData = {
      access_token: authToken,
      expires_in: 86399,
    };
    AuthManager.removeLocalStorageData();
    AuthManager.persistFullToken(tokenData);
    AuthManager.persistUserToLocalStorage({ merchant_uuid });
  }
}

function* fetchAccountDetailsWorker() {
  const accountUuid = yield select(selectors.getAccountUuid);
  try {
    let response = null;
    if (accountUuid) {
      response = yield call([axios, "get"], API_ENDPOINTS.SUBMIT_SIGNING_AUTHORITY(accountUuid)) || {};
    }
    if (response && response.data) yield put(actions.updateMerchantDetails(response.data));
    yield put(actions.fetchKYCStatus());
  } catch {
    yield put(uiActions.loadUI(yield select(selectors.getMerchantDetails), true));
  }
}

function* fetchKYCStatusWorker() {
  try {
    let response = yield call([axios, "get"], API_ENDPOINTS.KYC.STATUS(AuthManager.getMerchantUUID())) || {};
    if (response && response.data) yield put(actions.updateMerchantDetails(response.data));
    yield put(uiActions.loadUI(yield select(selectors.getMerchantDetails), true));
  } catch {
    yield put(uiActions.loadUI(yield select(selectors.getMerchantDetails), true));
  }
}

//  Sagas
export function* saga() {
  yield takeLatest(actionTypes.fetch, fetchWorker);
  yield takeLatest(actionTypes.saveAllowedQueryParams, saveAllowedQueryParamsWorker);
  yield takeLatest(actionTypes.fetchAccountDetails, fetchAccountDetailsWorker);
  yield takeLatest(actionTypes.fetchKYCStatus, fetchKYCStatusWorker);
}
